// Shared console components. Built with h() and createElementNS only.
// Markup strings are never parsed. Hostile strings are text nodes.

import { h } from "../dom.js";
import { ICON_PATHS, isIconName, type IconName } from "./icons.js";

export type BannerKind = "ok" | "warn" | "crit" | "info";
export type ButtonTone = "primary" | "secondary" | "quiet" | "destructive";

const SVG_NS = "http://www.w3.org/2000/svg";

export function svgEl(
  tag: string,
  attrs: Record<string, string> = {},
  children: readonly SVGElement[] = [],
): SVGElement {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [name, value] of Object.entries(attrs)) {
    node.setAttribute(name, value);
  }
  for (const child of children) node.append(child);
  return node;
}

export function svgIcon(name: IconName): SVGElement {
  const path = svgEl("path", {
    d: ICON_PATHS[name],
    fill: "none",
    stroke: "currentColor",
    "stroke-width": "1.8",
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
  });
  const svg = svgEl(
    "svg",
    {
      viewBox: "0 0 24 24",
      width: "20",
      height: "20",
      "aria-hidden": "true",
      class: "icon",
    },
    [path],
  );
  return svg;
}

/** Glyph span: a known icon name draws SVG; anything else is text. */
export function navGlyph(glyph: string): HTMLElement {
  if (isIconName(glyph)) {
    return h("span", { class: "nav-glyph", "aria-hidden": "true" }, [svgIcon(glyph)]);
  }
  return h("span", { class: "nav-glyph", "aria-hidden": "true" }, [glyph]);
}

export function pageHeader(
  title: string,
  subtitle?: string,
  actions?: HTMLElement,
): HTMLElement {
  const children: Array<HTMLElement | string> = [
    h("h1", { class: "page-title" }, [title]),
  ];
  if (subtitle !== undefined) {
    children.push(h("p", { class: "lede" }, [subtitle]));
  }
  const header = h("header", { class: "page-header", "data-role": "page-header" }, children);
  if (actions !== undefined) {
    header.append(h("div", { class: "page-header-actions" }, [actions]));
  }
  return header;
}

export function card(...children: Array<Node | string>): HTMLElement {
  return h("section", { class: "card", "data-role": "card" }, children);
}

export function statTile(
  label: string,
  value: string,
  sub?: string,
  onClick?: () => void,
): HTMLElement {
  const body: Array<Node | string> = [
    h("p", { class: "stat-label" }, [label]),
    h("p", { class: "stat-value" }, [value]),
  ];
  if (sub !== undefined) body.push(h("p", { class: "stat-sub" }, [sub]));
  if (onClick === undefined) {
    return h("div", { class: "stat-tile", "data-role": "stat-tile" }, body);
  }
  const button = h(
    "button",
    { type: "button", class: "stat-tile stat-tile-button", "data-role": "stat-tile" },
    body,
  );
  button.addEventListener("click", () => {
    onClick();
  });
  return button;
}

export function chip(status: string, text?: string): HTMLElement {
  return h(
    "span",
    { class: `chip chip-${status}`, "data-role": "chip", "data-status": status },
    [text ?? status],
  );
}

export function pill(status: string, text?: string): HTMLElement {
  return chip(status, text);
}

export function banner(kind: BannerKind, text: string, action?: HTMLElement): HTMLElement {
  const iconName: IconName = kind === "ok" ? "check" : "alert";
  const row = h(
    "div",
    { class: `banner banner-${kind}`, role: "status", "data-role": "component-banner", "data-kind": kind },
    [h("span", { class: "banner-icon", "aria-hidden": "true" }, [svgIcon(iconName)]), h("p", {}, [text])],
  );
  if (action !== undefined) row.append(action);
  return row;
}

export function button(label: string, tone: ButtonTone, attrs: Record<string, string> = {}): HTMLButtonElement {
  const className = ["btn", `btn-${tone}`, attrs["class"] ?? ""].filter((part) => part !== "").join(" ");
  const next: Record<string, string> = { ...attrs, type: attrs["type"] ?? "button", class: className };
  return h("button", next, [label]);
}

export function field(label: string, attrs: Record<string, string> = {}): HTMLElement {
  const input = h("input", { type: "text", class: "field-input", ...attrs });
  return h("label", { class: "field" }, [h("span", { class: "field-label" }, [label]), input]);
}

export function selectField(
  label: string,
  options: readonly { value: string; label: string }[],
  attrs: Record<string, string> = {},
): HTMLElement {
  const select = h("select", { class: "field-input", ...attrs });
  for (const option of options) {
    select.append(h("option", { value: option.value }, [option.label]));
  }
  return h("label", { class: "field" }, [
    h("span", { class: "field-label" }, [label]),
    h("span", { class: "select-shell" }, [
      select,
      h("span", { class: "select-chevron", "aria-hidden": "true" }, [svgIcon("chevron")]),
    ]),
  ]);
}

export function comboboxShell(label: string, attrs: Record<string, string> = {}): HTMLElement {
  const input = h("input", {
    type: "text",
    class: "field-input",
    role: "combobox",
    "aria-autocomplete": "list",
    "aria-expanded": "false",
    ...attrs,
  });
  return h("label", { class: "field" }, [
    h("span", { class: "field-label" }, [label]),
    h("span", { class: "select-shell" }, [
      input,
      h("span", { class: "select-chevron", "aria-hidden": "true" }, [svgIcon("chevron")]),
    ]),
  ]);
}

export function checkboxRow(label: string, attrs: Record<string, string> = {}): HTMLElement {
  const input = h("input", { type: "checkbox", ...attrs });
  return h("label", { class: "checkbox-row" }, [
    input,
    h("span", { class: "checkbox-label" }, [label]),
  ]);
}

export function dataTable(
  headers: readonly string[],
  rows: readonly (readonly string[])[],
  numericColumns: readonly number[] = [],
): HTMLElement {
  const numeric = new Set(numericColumns);
  const head = h(
    "tr",
    {},
    headers.map((header, index) =>
      h("th", { class: numeric.has(index) ? "num" : "", scope: "col" }, [header]),
    ),
  );
  const body = h(
    "tbody",
    {},
    rows.map((row) =>
      h(
        "tr",
        {},
        row.map((cell, index) => h("td", { class: numeric.has(index) ? "num" : "" }, [cell])),
      ),
    ),
  );
  const table = h("table", { class: "data-table" }, [h("thead", {}, [head]), body]);
  return h("div", { class: "table-scroll", "data-role": "data-table" }, [table]);
}

export function listRow(title: string, detail?: string, onClick?: () => void): HTMLElement {
  const text: Array<Node | string> = [h("span", { class: "list-row-title" }, [title])];
  if (detail !== undefined) text.push(h("span", { class: "list-row-detail" }, [detail]));
  if (onClick === undefined) {
    return h("div", { class: "list-row", "data-role": "list-row" }, text);
  }
  const button = h(
    "button",
    { type: "button", class: "list-row list-row-button", "data-role": "list-row" },
    text,
  );
  button.addEventListener("click", () => {
    onClick();
  });
  return button;
}

export function disclosure(summary: string, body: HTMLElement): HTMLElement {
  const details = h("details", { class: "disclosure", "data-role": "disclosure" }, [
    h("summary", {}, [summary]),
  ]);
  details.append(h("div", { class: "disclosure-body" }, [body]));
  return details;
}

export function drawer(title: string, body: HTMLElement, onClose: () => void): HTMLElement {
  const opener =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;
  let closed = false;
  function finish(): void {
    if (closed) return;
    closed = true;
    document.removeEventListener("keydown", onKey);
    onClose();
    if (opener !== null && opener.isConnected) opener.focus();
  }
  function onKey(event: KeyboardEvent): void {
    if (event.key === "Tab") {
      keepTabInside(event);
      return;
    }
    if (event.key !== "Escape") return;
    event.preventDefault();
    finish();
  }
  /**
   * The drawer says aria-modal, so Tab must not walk out of it into the page
   * behind it (round eight, Lens B): wrap at either end, and pull focus back in
   * if it was somewhere else.
   */
  function keepTabInside(event: KeyboardEvent): void {
    if (closed || !aside.isConnected) return;
    const stops = focusableWithin(aside);
    if (stops.length === 0) {
      event.preventDefault();
      aside.focus();
      return;
    }
    const first = stops[0] as HTMLElement;
    const last = stops[stops.length - 1] as HTMLElement;
    const active = document.activeElement;
    if (!(active instanceof Node) || !aside.contains(active) || active === aside) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus();
      return;
    }
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }
  const close = h(
    "button",
    { type: "button", class: "btn btn-quiet", "data-action": "close-drawer", "aria-label": "Close" },
    ["Close"],
  );
  close.addEventListener("click", finish);
  const aside = h(
    "aside",
    {
      class: "drawer",
      role: "dialog",
      "aria-modal": "true",
      "aria-label": title,
      "data-role": "drawer",
      tabindex: "-1",
    },
    [h("header", { class: "drawer-header" }, [h("h2", {}, [title]), close]), body],
  );
  document.addEventListener("keydown", onKey);
  // Move focus inside once the drawer is attached. The Close control is the
  // first focusable thing, so Escape and Tab both have a sane starting point.
  queueMicrotask(() => {
    if (closed || !aside.isConnected) return;
    const first = aside.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    (first ?? aside).focus();
  });
  return aside;
}

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/** The controls a keyboard user can Tab to inside `scope`, in document order. */
export function focusableWithin(scope: HTMLElement): HTMLElement[] {
  return Array.from(scope.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.closest("[hidden]") === null,
  );
}

/**
 * After a write takes away the control that had focus (the row is removed, the
 * button is disabled), focus falls to the page body and a keyboard or screen
 * reader user starts over from the top. Put it on something a person can
 * continue from, and say so by making the target focusable if it is not.
 */
export function focusAfterWrite(target: HTMLElement | null): void {
  if (target === null || !target.isConnected) return;
  if (target.getAttribute("tabindex") === null && !target.matches(FOCUSABLE)) {
    target.setAttribute("tabindex", "-1");
  }
  target.focus();
}

/**
 * Focus for after `removed` leaves a list: the next row's first control, else
 * the previous row's, else `fallback`. Call it AFTER the row is gone and pass
 * the neighbours read BEFORE (a removed node has no siblings).
 */
export function neighbourFocusTarget(removed: HTMLElement): HTMLElement | null {
  const sibling = removed.nextElementSibling ?? removed.previousElementSibling;
  if (!(sibling instanceof HTMLElement)) return null;
  return focusableWithin(sibling)[0] ?? sibling;
}

/**
 * Two controls whose accessible names would read identically cannot be told
 * apart by ear. Where a list can hold such a pair (two keys for one company
 * expiring the same day, two identities with one label), each of the identical
 * names gains an ordinal: "(1 of 2)", "(2 of 2)".
 *
 * Safe to call after every render and after every prepend: the base name is
 * remembered on the element, so a later identical neighbour renumbers the whole
 * group instead of suffixing a suffix.
 */
export function distinctControlNames(scope: ParentNode, selector: string): void {
  const controls = Array.from(scope.querySelectorAll<HTMLElement>(selector));
  const groups = new Map<string, HTMLElement[]>();
  for (const control of controls) {
    const base = control.dataset["baseName"] ?? control.getAttribute("aria-label") ?? "";
    if (base.length === 0) continue;
    control.dataset["baseName"] = base;
    const group = groups.get(base);
    if (group === undefined) groups.set(base, [control]);
    else group.push(control);
  }
  for (const [base, group] of groups) {
    const total = group.length;
    group.forEach((control, index) => {
      const position = `${String(index + 1)} of ${String(total)}`;
      control.setAttribute(
        "aria-label",
        total === 1 ? base : `${base} (${position})`,
      );
    });
  }
}

export function segmented(
  label: string,
  options: readonly { id: string; label: string }[],
  selected: string,
  onChange: (id: string) => void,
): HTMLElement {
  const group = h("div", {
    class: "segmented",
    role: "tablist",
    "aria-label": label,
    "data-role": "segmented",
  });
  for (const option of options) {
    const pressed = option.id === selected;
    const btn = h(
      "button",
      {
        type: "button",
        class: pressed ? "segmented-btn active" : "segmented-btn",
        role: "tab",
        "aria-selected": pressed ? "true" : "false",
        "data-segment": option.id,
      },
      [option.label],
    );
    btn.addEventListener("click", () => {
      onChange(option.id);
    });
    group.append(btn);
  }
  return group;
}

export function dateRangePresets(onPick: (preset: "this-month" | "last-month" | "this-year") => void): HTMLElement {
  const presets = [
    { id: "this-month" as const, label: "This month" },
    { id: "last-month" as const, label: "Last month" },
    { id: "this-year" as const, label: "This year" },
  ];
  const row = h("div", { class: "date-presets", "data-role": "date-presets", role: "group", "aria-label": "Date range" });
  for (const preset of presets) {
    const btn = h(
      "button",
      { type: "button", class: "btn btn-quiet", "data-preset": preset.id },
      [preset.label],
    );
    btn.addEventListener("click", () => {
      onPick(preset.id);
    });
    row.append(btn);
  }
  return row;
}

export function skeleton(lines = 3): HTMLElement {
  const host = h("div", { class: "skeleton", "data-role": "skeleton", "aria-hidden": "true" });
  for (let i = 0; i < lines; i += 1) {
    host.append(h("span", { class: "skeleton-line" }));
  }
  return host;
}

export function toast(message: string): HTMLElement {
  return h("p", { class: "toast", role: "status", "data-role": "toast" }, [message]);
}

export function emptyState(message: string, action?: HTMLElement): HTMLElement {
  const block = h("div", { class: "empty-state", "data-role": "empty-state" }, [
    h("p", {}, [message]),
  ]);
  if (action !== undefined) block.append(action);
  return block;
}

export function errorState(
  plainSentence: string,
  onRetry: () => void,
  details?: string,
): HTMLElement {
  const retry = h(
    "button",
    { type: "button", class: "btn btn-primary", "data-action": "retry", "aria-label": "Try again" },
    ["Try again"],
  );
  retry.addEventListener("click", () => {
    onRetry();
  });
  const body: Array<HTMLElement> = [
    h("h2", { class: "error-state-title" }, ["We could not load this page"]),
    h("p", {}, [plainSentence]),
    retry,
  ];
  if (details !== undefined && details.trim() !== "") {
    body.push(disclosure("Show details", h("p", {}, [details])));
  }
  return h("div", { class: "error-state", role: "alert", "data-role": "error-state" }, body);
}
